import { PriceRow } from 'server/types';
import * as fmt from 'shared/payments/fmt';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { IcoGift } from '@components/icons';

dayjs.extend(utc);

/**
 * Reframes an in-app AIP join for a cohort buyer who is entitled to free months:
 * instead of "pay $97/mo now", it shows the free-months benefit, when the first
 * charge lands, and the post-trial price. Rendered only when freeTrialDays > 0 —
 * non-entitled buyers keep the standard founding-rate checkout.
 */
export function AipTrialClaimSummary({
  price,
  freeTrialDays,
  firstChargeAt,
}: {
  price: Pick<PriceRow, 'priceInCents' | 'currency' | 'interval'>;
  freeTrialDays: number;
  firstChargeAt?: string;
}) {
  const interval = price.interval ? `/${price.interval}` : '';
  const chargeDate = firstChargeAt ? dayjs.utc(firstChargeAt).format('MMMM D, YYYY') : undefined;

  return (
    <div class="rounded-lg border border-green-200 bg-green-50 p-4 text-left dark:border-green-900 dark:bg-green-950">
      <div class="flex items-center gap-2">
        <IcoGift class="size-5 shrink-0 text-green-600 dark:text-green-400" />
        <span class="font-semibold text-green-800 dark:text-green-200">
          {fmt.trialLengthLabel(freeTrialDays)} of AI Playground
        </span>
      </div>
      <p class="mt-1 text-sm text-green-800/80 dark:text-green-200/80">
        Included with your course purchase.
        {chargeDate
          ? ` Your first payment of ${fmt.price(price)}${interval} is ${chargeDate} — cancel anytime before then and you won't be charged.`
          : ` Then ${fmt.price(price)}${interval} — cancel anytime.`}
      </p>
    </div>
  );
}
